import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpBackend } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PresignedUrlResponse {
  presignedUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private http = inject(HttpClient);
  // Inject the HttpBackend to create an HttpClient without interceptors
  private httpBackend = inject(HttpBackend);
  private httpNoInterceptor = new HttpClient(this.httpBackend);

  getPresignedUrl(fileName: string): Observable<PresignedUrlResponse> {
    const params = {
      s3bucket: environment.s3BucketLink,
      fileName: fileName
    };
    return this.http.get<PresignedUrlResponse>(environment.URL_ATTACHMENTS_GET_PRE_SIGNED, { params });
  }

  /**
   * Mint a short-lived presigned GET url for an object already in the attachments bucket.
   *
   * The attachments bucket is not publicly readable, so every read has to go through a
   * presigned url. These expire (~15 minutes) — mint one at view time and never persist it.
   *
   * @param s3Key The object key (e.g. "contracts/ABC123/1787083116156_x.pdf"), NOT a full url.
   */
  async getPresignedGetUrl(s3Key: string): Promise<string> {
    const params = {
      s3bucket: environment.s3BucketLink,
      fileName: s3Key,
      operation: 'get'
    };
    const response = await firstValueFrom(
      this.http.get<PresignedUrlResponse>(environment.URL_ATTACHMENTS_GET_PRE_SIGNED, { params })
    );

    if (!response?.presignedUrl) {
      throw new Error(`Presign endpoint returned no presignedUrl for key: ${s3Key}`);
    }

    return response.presignedUrl;
  }

  /**
   * Uploads a file to S3 using a pre-signed URL by bypassing all interceptors.
   * @param preSignedUrl The pre-signed URL to use for the upload.
   * @param file The file object to upload.
   * @returns An observable of the HTTP PUT request.
   */
  uploadFileToS3(preSignedUrl: string, file: File): Observable<any> {
    // Use the new HttpClient instance that bypasses all interceptors
    return this.httpNoInterceptor.put(preSignedUrl, file);
  }
}